// InventoryItemPage.tsx
import {
  Button,
  Checkbox,
  ColumnLayout,
  ContentLayout,
  Form,
  FormField,
  Header,
  Input,
  InputProps,
  SpaceBetween,
} from '@cloudscape-design/components';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchItemsByIds } from '../api/generic';
import {
  adjustInventoryQuantity,
  createInventoryItem,
  fetchInventoryItemWithLogs,
  formatLogLine,
  updateInventoryItem,
} from '../api/inventory';
import InventoryLogChart from '../components/InventoryLogChart';
import MultiCategorySuggest from '../components/MultiCategorySuggest';
import { useAuthContext } from '../contexts/AuthContext';
import { InventoryItem, InventoryLogStored } from '../models/Inventory';
import { UserData } from '../models/UserData';
import { toDate } from '../utils/timestamp';

import {
  UrlLink,
  addUrls,
  buildCreatePayload,
  buildUpdatedItem,
  capacityValid,
  coerceUrls,
  normalizeUrlsForSave,
  openExternal,
  parseNum,
  removeUrlAt,
  updateUrlLabel,
  updateUrlLink,
} from './InventoryItemPage.logic';

const InventoryItemPage: FC = () => {
  const { id } = useParams();
  const { tenant, userObject } = useAuthContext();
  const navigate = useNavigate();
  const [users, setUsers] = useState<Record<string, string>>({});

  const [item, setItem] = useState<InventoryItem | null>(null);
  const [quantity, setQuantity] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [validate, setValidate] = useState<boolean>(false);
  const [sugarContent, setSugarContent] = useState<string>('');
  const [capacity, setCapacity] = useState<string>(''); // for capacityL

  const [submitting, setSubmitting] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const initItem: InventoryItem = {
    id: 'new',
    name: '',
    unit: '',
    isContainer: false,
    quantity: 0,
    price: 0,
    sugarContentPer100g: 0,
    capacityL: 0,
    urls: [],
    categories: [],
  };

  useEffect(() => {
    (async () => {
      if (!id || id === 'new') return;
      const i = await fetchInventoryItemWithLogs(id, tenant);
      if (!i) {
        navigate('/Inventory');
        return;
      }
      const coerced = { ...i, urls: coerceUrls((i as any).urls) };
      setItem(coerced);
      setQuantity(coerced.quantity?.toString() ?? '');
      setPrice(coerced.price?.toString() ?? '');
      setSugarContent(coerced.sugarContentPer100g?.toString() ?? '');
      setCapacity(coerced.capacityL != null ? coerced.capacityL.toString() : '');
      setValidate(true);
    })();
  }, [id, tenant, navigate]);

  useEffect(() => {
    if (!item?.log || item.log.length === 0) return;

    const fetchUsers = async () => {
      const uniqueIds = Array.from(new Set((item?.log ?? []).map((l) => l.userId).filter(Boolean))) as string[];
      if (uniqueIds.length === 0) return;

      const userDocs = await fetchItemsByIds<UserData>('users', uniqueIds);
      const map: Record<string, string> = {};
      userDocs.forEach((u) => {
        if (u.id !== undefined) {
          map[u.id] = u.name?.split(' ')[0] ?? u.email ?? u.id ?? '';
        }
      });
      setUsers(map);
    };

    fetchUsers();
  }, [item?.log]);

  const submitForm = async () => {
    setValidate(true);
    if (!item) return;
    if (!item.name?.trim()) return;
    if (!item.unit?.trim()) return;
    if (item.isContainer && !capacityValid(item.isContainer, capacity)) return;

    setSubmitting(true);

    try {
      const normalizedUrls = normalizeUrlsForSave((item?.urls ?? []) as UrlLink[]);
      const newQty = parseNum(quantity);
      const newPrice = price.trim() ? parseNum(price) : undefined;
      const newSugar = sugarContent.trim() ? parseNum(sugarContent) : undefined;
      const newCapacity =
        item.isContainer && capacityValid(item.isContainer, capacity) ? parseNum(capacity) : undefined;

      let savedId = id;

      if (id === 'new') {
        const payload = buildCreatePayload({
          item: { ...item, urls: normalizedUrls } as InventoryItem,
          newPrice,
          newSugar,
          newCapacity,
          normalizedUrls,
        });
        const createdId = await createInventoryItem(payload, tenant);
        savedId = createdId;

        await adjustInventoryQuantity(
          createdId,
          {
            operation: 'update',
            quantity: newQty,
            action: 'initial_stock',
            userId: userObject?.id,
          },
          tenant,
        );
      } else {
        const updatedItem = buildUpdatedItem({
          current: { ...item, urls: normalizedUrls } as InventoryItem,
          newPrice,
          newSugar,
          newCapacity,
          normalizedUrls,
        });
        await updateInventoryItem(updatedItem, tenant);

        if (newQty !== item.quantity) {
          await adjustInventoryQuantity(
            item.id,
            {
              operation: 'update',
              quantity: newQty,
              action: 'inventory_adjustment',
              userId: userObject?.id,
            },
            tenant,
          );
        }
      }

      navigate('/Inventory?success=true&itemId=' + savedId);
    } catch (error) {
      console.error('Error saving inventory item:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddKeyDown: InputProps['onKeyDown'] = ({ detail }) => {
    if (detail.keyCode === 13) {
      addUrls(setItem, newUrl, newLabel);
    }
  };

  return (
    <ContentLayout
      disableOverlap
      header={
        <Header variant="h1" description={id === 'new' ? 'Create new inventory item' : id}>
          {id !== 'new' ? 'Edit inventory item: ' + (item?.name ?? '') : 'New Inventory Item'}
        </Header>
      }
    >
      <ColumnLayout columns={2} variant="text-grid">
        <Form
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => window.history.back()} formAction="none" variant="link">
                Cancel
              </Button>
              <Button disabled={submitting} onClick={submitForm} variant="primary" loading={submitting}>
                Submit
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="vertical" size="l">
            <FormField label="Inventory item name">
              <Input
                invalid={validate && !item?.name}
                value={item?.name ?? ''}
                onChange={({ detail }) =>
                  setItem((prev) => (prev ? { ...prev, name: detail.value } : { ...initItem, name: detail.value }))
                }
              />
            </FormField>

            <FormField label="Quantity">
              <Input value={quantity} onChange={({ detail }) => setQuantity(detail.value)} />
            </FormField>

            <FormField label="Unit" description="e.g. kg, L, pcs">
              <Input
                invalid={validate && !item?.unit}
                value={item?.unit ?? ''}
                onChange={({ detail }) =>
                  setItem((prev) => (prev ? { ...prev, unit: detail.value } : { ...initItem, unit: detail.value }))
                }
              />
            </FormField>

            <FormField label="Price">
              <Input value={price} onChange={({ detail }) => setPrice(detail.value)} />
            </FormField>

            <FormField label="Categories">
              <MultiCategorySuggest
                tenant={tenant}
                values={item?.categories ?? []}
                onChange={(updatedCategories) =>
                  setItem((prev) =>
                    prev ? { ...prev, categories: updatedCategories } : { ...initItem, categories: updatedCategories },
                  )
                }
              />
            </FormField>

            <FormField label="Is a container (bottle/can/keg)" description="Defines release units for excise.">
              <Checkbox
                checked={item?.isContainer ?? false}
                onChange={({ detail }) => {
                  const checked = detail.checked;
                  setItem((prev) =>
                    prev
                      ? { ...prev, isContainer: checked, perContainer: checked ? false : prev.perContainer }
                      : { ...initItem, isContainer: checked, perContainer: checked ? false : initItem.perContainer },
                  );
                  if (!checked) setCapacity('');
                }}
              />
            </FormField>

            {item?.isContainer && (
              <FormField
                label="Capacity (L)"
                description="e.g. 0.33, 0.75, 20"
                errorText={
                  validate && !capacityValid(item.isContainer, capacity)
                    ? 'Capacity must be a positive number.'
                    : undefined
                }
              >
                <Input
                  value={capacity}
                  invalid={validate && !capacityValid(item.isContainer, capacity)}
                  placeholder="0.33"
                  onChange={({ detail }) => setCapacity(detail.value)}
                />
              </FormField>
            )}

            <FormField label="Consumed per container" description="e.g. labels, caps, carb drops">
              <Checkbox
                checked={item?.perContainer ?? false}
                onChange={({ detail }) => {
                  const checked = detail.checked;
                  setItem((prev) =>
                    prev
                      ? { ...prev, perContainer: checked, isContainer: checked ? false : prev.isContainer }
                      : { ...initItem, perContainer: checked, isContainer: checked ? false : initItem.isContainer },
                  );
                }}
              />
            </FormField>

            <FormField label="Sugar Content per 100g ≈ Brix">
              <Input
                placeholder="For addition to abv if fermentable"
                value={sugarContent}
                onChange={({ detail }) => setSugarContent(detail.value)}
              />
            </FormField>

            <FormField label="Supplier / Product URLs">
              <SpaceBetween size="xs">
                {(((item?.urls ?? []) as UrlLink[]) || []).map((u, i) => (
                  <SpaceBetween key={`${u.link}-${i}`} direction="horizontal" size="xs">
                    <Input
                      value={u.label}
                      placeholder="Label"
                      onChange={({ detail }) => updateUrlLabel(setItem, i, detail.value)}
                    />
                    <Input
                      value={u.link}
                      placeholder="https://supplier.com/product"
                      onChange={({ detail }) => updateUrlLink(setItem, i, detail.value)}
                      onBlur={() => updateUrlLink(setItem, i, u.link)}
                    />
                    <Button
                      iconName="external"
                      variant="icon"
                      ariaLabel="Open URL"
                      onClick={() => openExternal(u.link)}
                    />
                    <Button
                      iconName="close"
                      variant="icon"
                      ariaLabel="Remove URL"
                      onClick={() => removeUrlAt(setItem, i)}
                    />
                  </SpaceBetween>
                ))}

                <SpaceBetween direction="horizontal" size="xs">
                  <Input
                    value={newLabel}
                    placeholder="Label (optional)"
                    onChange={({ detail }) => setNewLabel(detail.value)}
                    onKeyDown={handleAddKeyDown}
                  />
                  <Input
                    value={newUrl}
                    placeholder="Paste URL(s)"
                    onChange={({ detail }) => setNewUrl(detail.value)}
                    onKeyDown={handleAddKeyDown}
                  />
                  <Button onClick={() => addUrls(setItem, newUrl, newLabel)}>Add</Button>
                </SpaceBetween>
              </SpaceBetween>
            </FormField>
          </SpaceBetween>
        </Form>

        <SpaceBetween direction="vertical" size="m">
          {item?.log && item.log.length > 0 && (
            <>
              <h3>Stock History</h3>
              <InventoryLogChart logs={item.log as InventoryLogStored[]} unit={item.unit} />
            </>
          )}
          {((item?.log as InventoryLogStored[]) ?? []).map((log, index) => {
            const dt = toDate(log.timestamp);
            const userName = log.userId ? (users[log.userId] ?? log.userId) : 'System';
            return (
              <div key={index + dt.toISOString()}>
                {index === 0 ? (
                  <b>{formatLogLine(log, userName, item?.unit ?? 'items')}</b>
                ) : (
                  formatLogLine(log, userName, item?.unit ?? 'items')
                )}
                <em> ({dt.toLocaleString('en-UK')})</em>
              </div>
            );
          })}
        </SpaceBetween>
      </ColumnLayout>
    </ContentLayout>
  );
};

export default InventoryItemPage;
